import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  ConvertQuoteDto,
  ConvertQuoteSplitDto,
  CreateQuoteDto,
  CreateQuoteLineItemDto,
  QuotesControllerListQuotesParams,
  UpdateQuoteDto,
  UpdateQuoteStatusDto,
} from '@trellis/api'
import type { BatchUpdateQuoteLineItems } from '@trellis/shared'

import { usePagedInfiniteQuery } from '@/hooks/usePagedInfiniteQuery'
import { pushEvent } from '@/lib/analytics'
import {
  addQuoteLineItem,
  archiveQuote,
  assignTagsToQuote,
  batchUpdateQuoteLineItems,
  convertQuote,
  convertQuoteSplit,
  createQuote,
  deleteQuote,
  getQuote,
  getQuotePreview,
  getQuoteRevisions,
  listQuoteLineItems,
  listQuotes,
  removeTagFromQuote,
  supersedeQuote,
  transitionQuoteStatus,
  unarchiveQuote,
  updateQuote,
} from '@/requests/quotes'

/**
 * Shared with the quotes list route's loader ({@link quotesInfiniteQueryOptions}) so a
 * prefetched loader cache entry and this hook's read land on the same query key.
 */
export function quotesInfiniteQueryOptions(
  options: QuotesControllerListQuotesParams = {},
) {
  return infiniteQueryOptions({
    queryKey: ['quotes', options],
    queryFn: ({ pageParam }) =>
      listQuotes({ ...options, cursor: pageParam ?? options.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
  })
}

export function useQuotesQuery(options: QuotesControllerListQuotesParams = {}) {
  const limit = options.limit ?? 20

  const query = useInfiniteQuery(quotesInfiniteQueryOptions(options))

  return { ...query, ...usePagedInfiniteQuery(query, limit) }
}

export function useQuoteQuery(id: string) {
  return useQuery({
    queryKey: ['quotes', id],
    queryFn: () => getQuote(id),
    enabled: !!id,
  })
}

export function useQuotePreviewQuery(id: string | null) {
  return useQuery({
    queryKey: ['quotes', id, 'preview'],
    queryFn: () => getQuotePreview(id!),
    enabled: !!id,
  })
}

export function useQuoteLineItemsQuery(quoteId: string) {
  return useQuery({
    queryKey: ['quotes', quoteId, 'line-items'],
    queryFn: () => listQuoteLineItems(quoteId),
    enabled: !!quoteId,
  })
}

export function useQuoteRevisionsQuery(id: string) {
  return useQuery({
    queryKey: ['quotes', id, 'revisions'],
    queryFn: () => getQuoteRevisions(id),
    enabled: !!id,
  })
}

export function useCreateQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateQuoteDto) => {
      const result = await createQuote(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      pushEvent('quote_created')
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
    onError: () => pushEvent('form_submit_failed', { form_id: 'quote' }),
  })
}

export function useUpdateQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateQuoteDto }) => {
      const result = await updateQuote(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', variables.id] })
    },
  })
}

export function useDeleteQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteQuote(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useTransitionQuoteStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateQuoteStatusDto }) => {
      const result = await transitionQuoteStatus(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      if (variables.data.status === 'SENT') pushEvent('quote_sent')
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', variables.id] })
      // Accepting a quote may auto-activate a PROSPECT client.
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useSupersedeQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await supersedeQuote(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useArchiveQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await archiveQuote(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', id] })
    },
  })
}

export function useUnarchiveQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await unarchiveQuote(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', id] })
    },
  })
}

export function useConvertQuoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ConvertQuoteDto }) => {
      const result = await convertQuote(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      pushEvent('quote_converted', { split: false })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', variables.id] })
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useConvertQuoteSplitMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ConvertQuoteSplitDto }) => {
      const result = await convertQuoteSplit(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      pushEvent('quote_converted', { split: true })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', variables.id] })
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useAddQuoteLineItemMutation(quoteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateQuoteLineItemDto) => {
      const result = await addQuoteLineItem(quoteId, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quotes', quoteId, 'line-items'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
    },
  })
}

export function useBatchUpdateQuoteLineItemsMutation(quoteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: BatchUpdateQuoteLineItems) => {
      const result = await batchUpdateQuoteLineItems(quoteId, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quotes', quoteId, 'line-items'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
    },
  })
}

export function useAssignQuoteTagsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ quoteId, tagIds }: { quoteId: string; tagIds: string[] }) =>
      assignTagsToQuote(quoteId, tagIds),
    onSuccess: async (_, { quoteId }) => {
      await queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
    },
  })
}

export function useRemoveQuoteTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ quoteId, tagId }: { quoteId: string; tagId: string }) =>
      removeTagFromQuote(quoteId, tagId),
    onSuccess: async (_, { quoteId }) => {
      await queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
    },
  })
}
